import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Select, type SelectOption } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { analyticsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { campaignStatusMeta } from '@/lib/campaignStatus'
import { cardTitleStyle } from '@/lib/styleKit'
import { EmptyChart } from './EmptyChart'
import { PaperPlaneTilt, CaretLeft, CaretRight } from '@phosphor-icons/react'
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useReducedMotion } from 'framer-motion'
import CampaignDrillDown from './CampaignDrillDown'
import { seriesColor, useChartTheme } from './chartTheme'
import type { CampaignsData, Period } from '@/types/analytics'

interface TabProps {
  period: Period | null
  startDate: string | null
  endDate: string | null
  userId: number | null
  /** SMTP sender address selected in the page header (Campaigns scope). */
  senderEmail?: string
  /** True once the parent's sender list has loaded (gates the first fetch). */
  /** Passed straight through to the drill-down so the inbox filter is reachable
   *  while the drawer covers the page header. Changing it there changes the
   *  page's filter, so this list and the drawer stay in step. */
  mailboxOptions?: SelectOption[]
  mailboxValue?: string
  onMailboxChange?: (value: string) => void
}

const STATUSES = ['All', 'Scheduled', 'Running', 'Paused', 'Completed', 'Failed'] as const

// Pill chrome — single-select per §48
const pillIdle =
  'bg-card text-muted-foreground border-border/50 hover:bg-muted/40 hover:border-border/80 dark:bg-white/[0.02] dark:text-[#8a8f98] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]'
const pillActive =
  'bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40'

const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

/**
 * The three comparison series, in one place: the bars, the fixed legend and the
 * tooltip all read this list, so a metric cannot be one hue in the chart and
 * another in the legend.
 *
 * Hues come from the canonical scale via `seriesColor` (§61) — the private
 * `CHART_BLUE`/`CHART_AMBER`/`CHART_EMERALD` trio this file used to declare
 * painted `reply_rate` emerald, which is `delivered`'s hue everywhere else in
 * Analytics. `reply_rate` is violet now, matching the drill-down and the
 * deliverability breakdown.
 */
export const COMPARISON_SERIES = [
  { key: 'open_rate',  label: 'Open %'  },
  { key: 'click_rate', label: 'Click %' },
  { key: 'reply_rate', label: 'Reply %' },
] as const

/**
 * The sort choices, newest first and first in the list.
 *
 * Every other option is a performance metric, and a campaign that has just
 * launched has no performance yet: on a real account the newest of 400
 * campaigns sat at position 239 under open rate and 78 under sent, so the one
 * someone had just run was the one they could not find. `created_at` rather
 * than `actual_start` because it is never null -- a scheduled campaign sorts
 * to the top rather than being pushed to the end by NULLS LAST.
 */
export const CAMPAIGN_SORT_OPTIONS = [
  { value: 'created_at', label: 'Sort by newest' },
  { value: 'sent',       label: 'Sort by sent' },
  { value: 'open_rate',  label: 'Sort by open rate' },
  { value: 'click_rate', label: 'Sort by click rate' },
  { value: 'reply_rate', label: 'Sort by reply rate' },
] as const

/** What the tab opens on. */
export const DEFAULT_CAMPAIGN_SORT = 'created_at'

const PAGE_SIZE = 10

/**
 * A campaign that has sent nothing comes back with `null` rates, and the
 * unguarded `.toFixed` on them took the whole tab down. Not
 * `lib/utils#formatPercentage` — these values are already 0-100, not fractions.
 */
const pct = (v: number | null | undefined) => `${(v ?? 0).toFixed(1)}%`

// Custom tooltip — shows campaign name + Sent count to disambiguate
// identically-named campaigns, then each metric with its palette color.
function CampaignTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload || {}
  return (
    <div
      className="rounded-lg border bg-card dark:bg-[#191a1b] border-border/60 dark:border-white/[0.08] shadow-lg px-3 py-2"
      style={{ fontSize: '12px' }}
    >
      <div className="text-foreground dark:text-[#f7f8f8]" style={{ fontWeight: 590, marginBottom: 2 }}>
        {row.name}
      </div>
      <div className="text-muted-foreground dark:text-[#8a8f98]" style={{ marginBottom: 6 }}>
        Sent: <span className="tabular-nums">{(row.emails_sent ?? 0).toLocaleString()}</span>
      </div>
      <div className="space-y-1">
        {COMPARISON_SERIES.map(({ key, label }) => {
          const color = seriesColor(key)
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="inline-block rounded-full" style={{ width: 8, height: 8, backgroundColor: color }} />
              <span style={{ color }}>{label}</span>
              <span className="ml-auto tabular-nums text-foreground dark:text-[#f7f8f8]">
                {(row[key] ?? 0).toFixed(2)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Fixed legend — always lists Open / Click / Reply with their palette colors,
// even when the Click bar is hidden (all-zero) so the metric stays documented.
function ChartLegend() {
  return (
    <div className="flex items-center justify-center gap-4" style={{ fontSize: '12px' }}>
      {COMPARISON_SERIES.map(({ key, label }) => (
        <span key={key} className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]">
          <span className="inline-block rounded-full" style={{ width: 8, height: 8, backgroundColor: seriesColor(key) }} />
          {label}
        </span>
      ))}
    </div>
  )
}

// Single-line, ellipsis-truncated Y-axis label (full name shown in tooltip).
// Recharts clones this element per tick, injecting x / y / payload.
function CampaignYTick(props: any) {
  const { x, y, payload, fill } = props
  const label: string = payload?.value ?? ''
  const MAX = 24
  const text = label.length > MAX ? label.slice(0, MAX - 1).trimEnd() + '…' : label
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill={fill}
      fontSize={11}
      style={{ letterSpacing: '-0.1px' }}
    >
      {text}
    </text>
  )
}

export default function CampaignsTab({
  period, startDate, endDate, userId, senderEmail,
  mailboxOptions, mailboxValue, onMailboxChange,
}: TabProps) {
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const [statusFilter, setStatusFilter] = useState<string>('All')
  const [sortBy, setSortBy] = useState<string>(DEFAULT_CAMPAIGN_SORT)
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [page, setPage] = useState(1)

  // Reset to first page whenever filters/sort/period/mailbox change
  useEffect(() => { setPage(1) }, [statusFilter, sortBy, period, startDate, endDate, userId, senderEmail])

  const { data, isLoading, isError, error, isPreviousData } = useQuery<CampaignsData>({
    queryKey: ['analytics', 'campaigns', period, startDate, endDate, statusFilter, sortBy, page, userId, senderEmail],
    queryFn: () =>
      analyticsApi
        .getCampaigns({
          period: period || undefined,
          start: startDate || undefined,
          end: endDate || undefined,
          status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
          sort_by: sortBy,
          sort_dir: 'desc',
          // Paginate on the server so sort/period apply across ALL campaigns, not
          // just the first page the client happened to hold (AB#21864).
          page,
          page_size: PAGE_SIZE,
          user_id: userId ?? undefined,
          // Scope to the SMTP mailbox chosen in the page header (by address, so a
          // shared mailbox shows every sharing user's campaigns).
          sender_email: senderEmail,
        })
        .then((r) => r.data),
    // Never gated on the mailbox list. It used to wait for the parent's
    // sender addresses, and if that request failed the tab sat on a spinner
    // for good with every campaign hidden behind it. The default view is
    // "All inboxes" (no mailbox filter), so there is nothing to wait for.
    // `page` is part of the key, so without this every page change drops the tab
    // into the `isLoading` early-return below: the whole table (including the
    // pagination button that was just pressed) unmounts, dumping keyboard focus
    // on <body> and losing scroll position.
    keepPreviousData: true,
    staleTime: 5 * 1000,
    refetchOnWindowFocus: true,
  })

  // Clamp the page *state*, not just its rendered copy. The old render-time
  // `Math.min(page, totalPages)` left the request pointing at a page the result
  // no longer has — a background refetch that shrank the result while the user
  // sat on the last page returned an empty table under a footer reading
  // "11–10 of 12".
  const totalPages = data ? Math.max(1, Math.ceil(data.total_count / PAGE_SIZE)) : 1
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#5e6ad2] dark:text-[#a5b4fc]">
        <DotLoader size="lg" label="Loading campaign analytics" />
      </div>
    )
  }

  if (isError) {
    const detail = (error as any)?.response?.data?.detail
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          Could not load campaign analytics
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          {typeof detail === 'string' ? detail : 'Please try again.'}
        </p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <PaperPlaneTilt className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          No campaign data
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No campaign data available for this period.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Filter Bar */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-xl border p-3',
          'bg-card border-border/50',
          'dark:bg-white/[0.02] dark:border-white/[0.08]',
        )}
      >
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              aria-pressed={statusFilter === s}
              className={cn(
                'inline-flex items-center px-3 py-1.5 rounded-full border transition-colors duration-150',
                statusFilter === s ? pillActive : pillIdle,
              )}
              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Select
            value={sortBy}
            onChange={(v) => setSortBy(v)}
            ariaLabel="Sort campaigns"
            options={[...CAMPAIGN_SORT_OPTIONS]}
            minWidth={180}
          />
        </div>
      </div>

      {/* Comparison Chart */}
      {data.comparison_chart.length > 0 && (() => {
        const totalMagnitude = data.comparison_chart.reduce(
          (s, c) => s + (c.open_rate || 0) + (c.click_rate || 0) + (c.reply_rate || 0),
          0,
        )
        const hasAnyData = totalMagnitude > 0
        // Only render the Click series when at least one campaign has clicks —
        // otherwise the always-zero bar reserves an empty slot that splits each
        // campaign's Open/Reply pair with a confusing gap. The series stays in
        // the fixed legend either way, so the metric is still documented.
        const hasClickData = data.comparison_chart.some((c) => (c.click_rate || 0) > 0)
        return (
          // `keepPreviousData` keeps the last result on screen while the next
          // page/filter loads, so the surface has to say it is stale rather than
          // presenting old numbers as current.
          <Card className={cn(cardCls, isPreviousData && 'opacity-60')} aria-busy={isPreviousData}>
            <CardHeader>
              <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
                Campaign comparison (top {data.comparison_chart.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasAnyData ? (
                // `role="group"` so the name is actually exposed: `aria-label` on
                // a bare div (role=generic) is dropped by AT, and `role="img"`
                // would hide the bars recharts' accessibilityLayer just made
                // keyboard-explorable.
                <div role="group" aria-label="Open, click and reply rate per campaign, as a percentage of emails sent">
                  <ResponsiveContainer width="100%" height={Math.max(260, data.comparison_chart.length * 44) + 50}>
                    <BarChart
                      accessibilityLayer
                      data={data.comparison_chart}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 12, bottom: 8 }}
                      barGap={2}
                      barCategoryGap="24%"
                    >
                      <CartesianGrid horizontal={false} stroke={chartTheme.grid} strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        tick={{ fontSize: 11, fill: chartTheme.tickFill }}
                        stroke={chartTheme.axis}
                        strokeWidth={1}
                        tickLine={false}
                        tickMargin={8}
                        unit="%"
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={188}
                        interval={0}
                        tick={<CampaignYTick fill={chartTheme.tickFill} />}
                        tickMargin={10}
                        stroke={chartTheme.axis}
                        strokeWidth={1}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip cursor={{ fill: chartTheme.grid }} content={<CampaignTooltip />} />
                      <Legend
                        wrapperStyle={{ paddingTop: 12 }}
                        content={<ChartLegend />}
                      />
                      {COMPARISON_SERIES.filter(({ key }) => key !== 'click_rate' || hasClickData).map(
                        ({ key, label }) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            name={label}
                            fill={seriesColor(key)}
                            fillOpacity={0.8}
                            radius={[0, 4, 4, 0]}
                            barSize={11}
                            maxBarSize={11}
                            isAnimationActive={!prefersReduced}
                          />
                        ),
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyChart message="Campaigns in this window have no engagement yet." icon={<PaperPlaneTilt className="h-5 w-5" weight="regular" />} />
              )}
            </CardContent>
          </Card>
        )
      })()}

      {/* Data Table */}
      {(() => {
        // Server-side pagination: `data.campaigns` is already the current page and
        // `data.total_count` is the full result size across all pages.
        const total = data.total_count
        const currentPage = Math.min(page, totalPages)
        const startIdx = (currentPage - 1) * PAGE_SIZE
        const pageCampaigns = data.campaigns
        const startItem = total === 0 ? 0 : startIdx + 1
        const endItem = Math.min(startIdx + pageCampaigns.length, total)

        const pageNumbers: (number | 'ellipsis')[] = []
        if (totalPages <= 7) {
          for (let i = 1; i <= totalPages; i++) pageNumbers.push(i)
        } else {
          pageNumbers.push(1)
          if (currentPage > 3) pageNumbers.push('ellipsis')
          for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pageNumbers.push(i)
          if (currentPage < totalPages - 2) pageNumbers.push('ellipsis')
          pageNumbers.push(totalPages)
        }

        return (
      <Card className={cn(cardCls, isPreviousData && 'opacity-60')} aria-busy={isPreviousData}>
        <CardHeader>
          <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
            All campaigns ({data.total_count})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {pageCampaigns.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/50 dark:border-white/[0.06]">
                  {[
                    { label: 'Campaign',  align: 'left'  },
                    { label: 'Status',    align: 'left'  },
                    { label: 'Sent',      align: 'right' },
                    { label: 'Opened %',  align: 'right' },
                    { label: 'Clicked %', align: 'right' },
                    { label: 'Replied %', align: 'right' },
                    { label: 'Bounced %', align: 'right' },
                    { label: 'Unsub %',   align: 'right' },
                  ].map((h, i, arr) => (
                    <th
                      key={h.label}
                      scope="col"
                      className={cn(
                        'px-4 py-3 align-middle text-muted-foreground dark:text-[#8a8f98] uppercase',
                        h.align === 'right' ? 'text-right' : 'text-left',
                        i === arr.length - 1 && 'pr-6',
                        i === 0 && 'pl-6',
                      )}
                      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                {pageCampaigns.map((c) => {
                  // Label, tone and glyph from the §60 state map. The old local
                  // chip table had no `draft` entry and printed the raw enum
                  // through `capitalize`, so an unmapped state read
                  // "Partially_sent".
                  const status = campaignStatusMeta(c.status)
                  return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedCampaignId(c.id)}
                    className="group cursor-pointer transition-colors duration-150 hover:bg-muted/40 dark:hover:bg-white/[0.03]"
                  >
                    <td className="pl-6 pr-4 py-3 max-w-[220px]">
                      {/* The row keeps its click affordance for mouse users, but
                          the keyboard entry point must be a real control: the
                          drill-down used to be reachable by mouse only, and a
                          <tr role="button"> would break table semantics for
                          everyone. No `truncate` on the cell — its
                          `overflow:hidden` would clip the button's focus ring. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedCampaignId(c.id) }}
                        aria-haspopup="dialog"
                        title={c.name}
                        className="block max-w-full truncate rounded-sm text-left text-foreground dark:text-[#f7f8f8] group-hover:text-[#5e6ad2] dark:group-hover:text-[#a5b4fc] transition-colors"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {c.name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border',
                          status.borderChipClass,
                        )}
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                      >
                        <status.Icon className={cn('h-3 w-3', status.iconNudge)} weight="regular" aria-hidden="true" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.1px' }}>
                      {(c.sent ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {pct(c.open_rate)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {pct(c.click_rate)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {pct(c.reply_rate)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {pct(c.bounce_rate)}
                    </td>
                    <td className="pl-4 pr-6 py-3 text-right tabular-nums text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {pct(c.unsubscribe_rate)}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <EmptyChart message="No campaigns match the current filters." icon={<PaperPlaneTilt className="h-5 w-5" weight="regular" />} />
          )}

          {/* Pagination — always visible when there are rows; nav disabled on single page */}
          {total > 0 && (
            <div className="flex items-center justify-between gap-3 px-6 pt-3 border-t border-border/50 dark:border-white/[0.06] mt-1">
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
                  {startItem}–{endItem}
                </span>{' '}
                of{' '}
                <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
                  {total}
                </span>{' '}
                campaigns
              </p>
              <nav className="flex items-center gap-1" aria-label="Campaign table pages">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                  aria-label="Previous page"
                >
                  <CaretLeft className="h-4 w-4" weight="bold" />
                </button>
                {pageNumbers.map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-1.5 text-muted-foreground/70 dark:text-[#62666d]"
                      style={{ fontSize: '12px' }}
                      aria-hidden="true"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      aria-label={`Page ${p}`}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={cn(
                        'h-7 min-w-[1.75rem] px-2 rounded-md transition-colors duration-150 tabular-nums',
                        p === currentPage
                          ? 'bg-[#5e6ad2] text-white dark:bg-[#5e6ad2]'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]',
                      )}
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                  aria-label="Next page"
                >
                  <CaretRight className="h-4 w-4" weight="bold" />
                </button>
              </nav>
            </div>
          )}
        </CardContent>
      </Card>
        )
      })()}

      {/* Drill-Down Panel */}
      <CampaignDrillDown
        campaignId={selectedCampaignId}
        onClose={() => setSelectedCampaignId(null)}
        userId={userId}
        period={period}
        startDate={startDate}
        endDate={endDate}
        senderEmail={senderEmail}
        mailboxOptions={mailboxOptions}
        mailboxValue={mailboxValue}
        onMailboxChange={onMailboxChange}
      />
    </div>
  )
}
